/**
 * Decision list query (Decisions v4 redesign) — the search and sort the
 * Decisions tab applies on top of the oldest-first slice. Phase/trade
 * filtering stays the shared exact-match `matchesTaggedReadFilter`
 * (read/tag-filter.ts). Pure functions only; DecisionLogView owns the state.
 *
 * Oldest-first stays the DEFAULT order and the base every other order starts
 * from: `sortDecisions` only re-orders an already oldest-first list, with a
 * stable sort, so ties always fall back to oldest-first. Nothing here ranks
 * or suggests — every order is one the human picked from the Sort menu.
 */

import type { Decision } from "./decision-slice";

// ---------- search ----------

/** The design's searched fields: title, room, decision maker and trade. */
export function matchesSearch(decision: Decision, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [decision.text, decision.room_zone, decision.decides, decision.trade].some(
    (field) => field !== undefined && field.toLowerCase().includes(q),
  );
}

/** Splits `text` into plain and matched runs so the list can mark the match. */
export function highlightParts(text: string, query: string): { text: string; match: boolean }[] {
  const q = query.trim().toLowerCase();
  if (!q) return [{ text, match: false }];
  const parts: { text: string; match: boolean }[] = [];
  const lower = text.toLowerCase();
  let from = 0;
  for (let at = lower.indexOf(q); at !== -1; at = lower.indexOf(q, from)) {
    if (at > from) parts.push({ text: text.slice(from, at), match: false });
    parts.push({ text: text.slice(at, at + q.length), match: true });
    from = at + q.length;
  }
  if (from < text.length) parts.push({ text: text.slice(from), match: false });
  return parts;
}

// ---------- sort ----------

export const SORT_OPTIONS = [
  { id: "oldest", label: "Oldest first" },
  { id: "newest", label: "Newest first" },
  { id: "unassigned", label: "Unassigned first" },
  { id: "decides", label: "Decision maker" },
  { id: "room", label: "Room" },
  { id: "trade", label: "Trade" },
] as const;

export type SortId = (typeof SORT_OPTIONS)[number]["id"];

export function sortLabel(sort: SortId): string {
  return SORT_OPTIONS.find((o) => o.id === sort)?.label ?? "Oldest first";
}

/** Text order with blanks last, so "no room" never crowds the top. */
function byText(pick: (d: Decision) => string | undefined) {
  return (a: Decision, b: Decision) => {
    const x = pick(a)?.trim().toLowerCase() ?? "";
    const y = pick(b)?.trim().toLowerCase() ?? "";
    if (!x || !y) return x ? -1 : y ? 1 : 0;
    return x.localeCompare(y);
  };
}

/** Re-orders an oldest-first list. Array.prototype.sort is stable, so equal
 * keys keep their oldest-first order. */
export function sortDecisions(oldestFirst: readonly Decision[], sort: SortId): Decision[] {
  switch (sort) {
    case "oldest":
      return [...oldestFirst];
    case "newest":
      return [...oldestFirst].reverse();
    case "unassigned":
      return [...oldestFirst].sort((a, b) => Number(!!a.owner) - Number(!!b.owner));
    case "decides":
      return [...oldestFirst].sort(byText((d) => d.decides));
    case "room":
      return [...oldestFirst].sort(byText((d) => d.room_zone));
    case "trade":
      return [...oldestFirst].sort(byText((d) => d.trade));
  }
}

// ---------- people ----------

/** Names already used as a responsible party on this project's decisions,
 * A–Z. Offered as a pick list for Assign; nothing is ever pre-selected. */
export function knownPeople(decisions: readonly Decision[]): string[] {
  const names = new Map<string, string>();
  for (const d of decisions) {
    for (const name of [d.owner, d.assignee]) {
      const trimmed = name?.trim();
      if (trimmed && !names.has(trimmed.toLowerCase())) names.set(trimmed.toLowerCase(), trimmed);
    }
  }
  return [...names.values()].sort((a, b) => a.localeCompare(b));
}

export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}
